import { z } from "zod";

import { UserMcpServerConnectionId } from "../../idTypeBrands.js";
import type { ThreadAutoApproveMcpToolIdentity } from "../../mcp-client/callMcpToolAgentArgs.js";
import { McpClientToolName } from "../../mcp-client/toolConfig.js";

import { TOOL_NAMES } from "./toolNames.js";

/** Identity for the persistent "Always allow this tool" choice. */
export const AskUserMcpToolApprovalContext = z.object({
  mcpServerConnectionId: UserMcpServerConnectionId,
  mcpToolName: McpClientToolName,
});
export type AskUserMcpToolApprovalContext = z.infer<
  typeof AskUserMcpToolApprovalContext
>;

/** Display type when an agent wants the user to confirm or deny something. */
export const AskUserConfirmDisplay = z.object({
  type: z.literal("confirm"),
  description: z.string(),
  confirmText: z.string().optional(),
  denyText: z.string().optional(),
});
export type AskUserConfirmDisplay = z.infer<typeof AskUserConfirmDisplay>;

/** Display type when an agent wants the user to approve an MCP tool call. */
export const AskUserMcpToolApprovalDisplay = z.object({
  type: z.literal("mcp-tool-approval"),
  description: z.string(),
  denyText: z.string().optional(),
  mcpToolApprovalContext: AskUserMcpToolApprovalContext,
});
export type AskUserMcpToolApprovalDisplay = z.infer<
  typeof AskUserMcpToolApprovalDisplay
>;

/** Display type when an agent needs the user to reauthenticate an OAuth data connection. */
export const AskUserOAuthDisplay = z.object({
  type: z.literal("oauth"),
  description: z.string(),
  dataConnectionId: z.string(),
  dataConnectionName: z.string(),
  dataConnectionType: z.string(),
  oAuthConnectionId: z.string(),
  authWithSso: z.boolean(),
  currentRole: z.string().or(z.null()).optional(),
  requiredRole: z.string().or(z.null()).optional(),
});
export type AskUserOAuthDisplay = z.infer<typeof AskUserOAuthDisplay>;

/** Display type when an agent needs the user to reauthenticate an MCP server. */
export const AskUserMcpReauthDisplay = z.object({
  type: z.literal("mcp-reauth"),
  mcpServerName: z.string(),
  mcpServerConnectionId: UserMcpServerConnectionId,
});
export type AskUserMcpReauthDisplay = z.infer<typeof AskUserMcpReauthDisplay>;

/** Display type for a multiple-choice question presented to the user. */
export const AskUserMultipleChoiceDisplay = z.object({
  type: z.literal("multiple-choice"),
  question: z.string(),
  options: z.array(
    z.object({ label: z.string(), description: z.string().optional() }),
  ),
  allowOther: z.boolean().optional(),
});
export type AskUserMultipleChoiceDisplay = z.infer<
  typeof AskUserMultipleChoiceDisplay
>;

export const AskUserConfirmResponse = z.enum(["CONFIRM", "DENY", "EDITED"]);
export type AskUserConfirmResponse = z.infer<typeof AskUserConfirmResponse>;

/** Sentinel string written into answers[] when the user doesn't respond in time. */
export const USER_QUESTION_TIMED_OUT = "User did not respond in time";

/**
 * Payload published by `respondToAskUserQuestionRequest` when the user answered
 * a multiple-choice question.
 */
export type QuestionAnsweredPayload = {
  type: "question-answered";
  answer: string;
};

/**
 * Payload published by `respondToAskUserQuestionRequest` when the user dismissed
 * a multiple-choice question (closed it without picking an option).
 */
export type QuestionDismissedPayload = {
  type: "question-dismissed";
};

/** Tagged payload shape used for ask-user question responses on the pub/sub wire. */
export type QuestionTopicPayload =
  | QuestionAnsweredPayload
  | QuestionDismissedPayload;

/**
 * Payload shape used for confirm/oauth ask responses on the pub/sub wire.
 *
 * Publishers should send the tagged-object variants. The bare-string
 * `"CONFIRM"`/`"DENY"`/`"EDITED"` forms are accepted by consumers for
 * backwards compatibility with publishers running pre-tagged-payload code
 * and are scheduled for removal after the 5-release deprecation window.
 * The `EDITED` variant carries an optional, tool-defined `editedEntityId`
 * the client can return (e.g. the id of a resource it just created) so the
 * tool's `onEdited` handler can reference it back to the agent.
 */
export type ConfirmTopicPayload =
  /** @deprecated Publishers should send `{ type: "CONFIRM" }`. Kept for wire compatibility. */
  | "CONFIRM"
  /** @deprecated Publishers should send `{ type: "DENY" }`. Kept for wire compatibility. */
  | "DENY"
  /** @deprecated Publishers should send `{ type: "EDITED" }`. Kept for wire compatibility. */
  | "EDITED"
  | { type: "CONFIRM" }
  | { type: "DENY" }
  | { type: "EDITED"; editedEntityId?: string };

/**
 * Wire type for `AgentAskUserResponseTopic`. Both the confirm and the question
 * resolvers publish tagged objects; consumers narrow with `.type`.
 */
export type AskUserTopicPayload = ConfirmTopicPayload | QuestionTopicPayload;

/**
 * User requests or "Ask User" messages represent a request from the agent to the user (e.g., such as permission
 * to continue executing the tool).
 */
export const AskUserDisplay = z.union([
  AskUserConfirmDisplay,
  AskUserMcpToolApprovalDisplay,
  AskUserOAuthDisplay,
  AskUserMcpReauthDisplay,
  AskUserMultipleChoiceDisplay,
]);
export type AskUserDisplay = z.infer<typeof AskUserDisplay>;

/**
 * Approval mode is pinned per run, not per thread: it is resolved when a prompt
 * is submitted and cannot change until that run ends. It determines what the
 * agent does when it hits a HITL check requiring approval (ex: confirm/deny).
 *
 * ASK_FOR_APPROVAL (default): The agent should ask the user for approval.
 * SKIP_APPROVALS: The agent should skip the approval and continue executing the tool.
 */
export const AgentApprovalMode = z.enum(["ASK_FOR_APPROVAL", "SKIP_APPROVALS"]);
export type AgentApprovalMode = z.infer<typeof AgentApprovalMode>;

/**
 * A block that represents the agent asking the user for permission to continue execution.
 *
 * This is a transient intermediary block similar to tool progress updates which is not
 * actually sent to the agent BUT could result in a "tool rejected by user" response which is.
 */
export const MessageAskUserBlock = z.object({
  type: z.literal("ask_user"),
  request: AskUserDisplay,
});
export type MessageAskUserBlock = z.infer<typeof MessageAskUserBlock>;

/**
 * Key under which a tool's per-thread auto-approve flag is stored in
 * {@link AskUserAutoApprovals}. Only produce values via
 * {@link getThreadAutoApproveKey} so the read and write sides agree.
 */
export const ThreadAutoApproveKey = z.string().brand("ThreadAutoApproveKey");
export type ThreadAutoApproveKey = z.infer<typeof ThreadAutoApproveKey>;

/**
 * Per-thread auto-approve map. Keys are usually a tool name, but for
 * `CallMcpTool` the key is a composite produced by {@link getThreadAutoApproveKey}
 * so "allow for this thread" can be scoped to a single MCP tool rather than all MCP tools.
 */
export type AskUserAutoApprovals = Partial<
  Record<ThreadAutoApproveKey, boolean>
>;

/**
 * Canonical derivation of the key used to read and write a tool's per-thread
 * auto-approve flag in {@link AskUserAutoApprovals}.
 *
 * For most tools this is just the tool name. For MCP it returns a composite
 * key for the tool so "allow for this thread" does not implicitly approve every
 * MCP tool. If the identity is missing it falls back to the bare tool name, but
 * approval checks deliberately ignore that legacy broad MCP key.
 */
export function getThreadAutoApproveKey(
  toolName: string,
  mcpTool?: ThreadAutoApproveMcpToolIdentity | null,
): ThreadAutoApproveKey {
  if (toolName === TOOL_NAMES.CALL_MCP_TOOL && mcpTool != null) {
    return ThreadAutoApproveKey.parse(
      `${toolName}::${JSON.stringify([
        mcpTool.mcpServerType,
        mcpTool.mcpServerName.toLowerCase(),
        mcpTool.mcpToolName,
      ])}`,
    );
  }
  return ThreadAutoApproveKey.parse(toolName);
}

export function isToolAutoApprovedForThread(
  autoApprovals: AskUserAutoApprovals | null,
  toolName: string,
  mcpTool?: ThreadAutoApproveMcpToolIdentity | null,
): boolean {
  if (autoApprovals == null) {
    return false;
  }
  const key = getThreadAutoApproveKey(toolName, mcpTool);
  // Ignore legacy blanket MCP approvals; MCP approval is per-tool now.
  if (key === TOOL_NAMES.CALL_MCP_TOOL) {
    return false;
  }
  return autoApprovals[key] === true;
}
